import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserRepository } from "../repositories/UserRepository";
import { IoCheckmarkSharp } from "react-icons/io5";
import { CustomSwitch } from "../common/CustomSwitch";
import { useDispatch } from "react-redux";
import { triggerRefresh } from "../../store/GlobalSlice";

export default function EditUser() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getById();
    getAllRoles();
  }, [id]);

  const getById = () => {
    UserRepository.getById(id)
      .then((res) => {
        setData(res.data);
        const extractedRoles = (res.data.roles || []).map((r) => r.role);
        setSelectedRoles(extractedRoles);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleChange = (field) => (event) => {
    setData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleStatusToggle = () => {
    setData((prev) => ({
      ...prev,
      status: prev.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
    }));
  };

  const handleSave = () => {
    if (!data) return;

    const updatedData = {
      ...data,
      selectedRoles: selectedRoles,
      roles: undefined,
    };

    setLoading(true);
    UserRepository.save(updatedData)
      .then(() => {
        setLoading(false);
        dispatch(triggerRefresh("users"));
        navigate("/users");
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const getAllRoles = () => {
    UserRepository.getAllRoles()
      .then((res) => {
        setRoles(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleToggle = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  return (
    <Dialog
      open
      onClose={() => navigate("/users")}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          display: "flex",
          flexDirection: "column",
        },
      }}
      style={{ zIndex: 1300 }}
    >
      <DialogTitle>Измени корисник</DialogTitle>
      <DialogContent
        style={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <CustomSwitch
                  sx={{ margin: "0 10px" }}
                  checked={data?.status === "ACTIVE"}
                  onChange={handleStatusToggle}
                />
              }
              label={data?.status === "ACTIVE" ? "Активен" : "Неактивен"}
            />
          </Grid>
          <Grid item xs={12} style={{ marginTop: "10px" }}>
            <TextField
              fullWidth
              size="small"
              label="Корисничко име"
              autoComplete="off"
              value={data?.username || ""}
              onChange={handleChange("username")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Име"
              autoComplete="off"
              value={data?.firstName || ""}
              onChange={handleChange("firstName")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Презиме"
              autoComplete="off"
              value={data?.lastName || ""}
              onChange={handleChange("lastName")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Е-пошта"
              autoComplete="off"
              value={data?.email || ""}
              onChange={handleChange("email")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Телефонски број"
              autoComplete="off"
              value={data?.phoneNumber || ""}
              onChange={handleChange("phoneNumber")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Титула"
              autoComplete="off"
              value={data?.title || ""}
              onChange={handleChange("title")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Контролор"
              autoComplete="off"
              value={data?.approvingUser || ""}
              onChange={handleChange("approvingUser")}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                flexDirection: "column",
                marginTop: "10px",
              }}
            >
              <Typography variant="h6" style={{ padding: "5px" }}>
                Улоги
              </Typography>
              <div style={{ gap: "8px", display: "flex", flexWrap: "wrap" }}>
                {roles.map((role) => {
                  const selected = selectedRoles.includes(role);
                  return (
                    <Chip
                      key={role}
                      label={
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: 12,
                            color: selected ? "#fff" : "#454545",
                          }}
                        >
                          {role}
                        </Typography>
                      }
                      icon={
                        selected ? (
                          <IoCheckmarkSharp
                            style={{ color: "#fff", fontSize: "16px" }}
                          />
                        ) : undefined
                      }
                      clickable
                      color={selected ? "primary" : "default"}
                      variant={selected ? "filled" : "outlined"}
                      onClick={() => handleToggle(role)}
                      sx={{
                        boxShadow: selected
                          ? "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px"
                          : "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow:
                            "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
                          backgroundColor: selected
                            ? "primary.dark"
                            : "grey.100",
                        },
                        cursor: "pointer",
                      }}
                    />
                  );
                })}
              </div>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "10px 25px" }}>
        <Button
          variant="outlined"
          onClick={() => {
            navigate("/users");
          }}
          color="primary"
          size="small"
          style={{ width: "120px" }}
        >
          <Typography>Откажи</Typography>
        </Button>
        <Button
          variant="contained"
          colors="primary"
          size="large"
          onClick={() => {
            handleSave();
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 80,
            }}
          >
            {!loading ? (
              <Typography>Зачувај</Typography>
            ) : (
              <CircularProgress size={20} id="circular-progress" />
            )}
          </Box>
        </Button>
      </DialogActions>
    </Dialog>
  );
}
